import { useCallback, useMemo, useState } from 'react';

import { LoadingIndicator } from '@bambi/component-library/src/components/LoadingIndicator/LoadingIndicator';

import { useAppDispatch, useAppSelector } from 'src/app/store/hooks';
import { useRoutesRouteTemplatesMultiloadListQuery } from 'src/common/external/bambi-api/routesApi';
import { useConfirmClose } from 'src/common/modals/RequiresConfirmationModal/RequiresConfirmationModal';
import { Modal } from 'src/common/primitives/Modal';

import { RouteTemplateForm } from '../forms/RouteTemplateForm';
import { routeTemplateActions } from '../routeTemplates.slice';

export function RouteTemplateFormModal() {
  const [isFormDirty, setFormDirty] = useState(false);
  const dispatch = useAppDispatch();
  const open = useAppSelector(
    (state) => state.routeTemplates.showRouteTemplateForm
  );
  const editingRouteTemplate = useAppSelector(
    (state) => state.routeTemplates.editingRouteTemplate
  );

  const {
    data: routeMultiloadTemplates,
    isLoading: isLoadingRouteMultiloadTemplates,
  } = useRoutesRouteTemplatesMultiloadListQuery(
    {
      routeTemplateId: editingRouteTemplate?.id ?? '',
    },
    {
      skip: !editingRouteTemplate?.id,
    }
  );

  const formValues = useMemo(() => {
    // Because we're skipping the refetch, this data may be cached from a
    // previous edit. So we need to clear it if we're creating a new template.
    let multiloadTripTemplates = routeMultiloadTemplates?.results ?? [];
    if (!editingRouteTemplate?.id) {
      multiloadTripTemplates = [];
    }

    // If we're presetting the multiload trip templates, we need to use them
    if (editingRouteTemplate?.multiload_trip_templates?.length) {
      multiloadTripTemplates = editingRouteTemplate.multiload_trip_templates;
    }

    return {
      ...editingRouteTemplate,
      multiload_trip_templates: multiloadTripTemplates,
    };
  }, [editingRouteTemplate, routeMultiloadTemplates]);

  const setOpenInternal = useCallback(
    (openState: boolean) => {
      dispatch(routeTemplateActions.setShowRouteTemplateForm(openState));
      if (!openState) {
        dispatch(routeTemplateActions.setWorkflow('single'));
        dispatch(routeTemplateActions.selectAssignment(undefined));
        dispatch(routeTemplateActions.resetSelectedTrips());
      }
    },
    [dispatch]
  );
  const shouldConfirmClose = useCallback(() => isFormDirty, [isFormDirty]);

  const [setOpen, onClose] = useConfirmClose({
    setOpen: setOpenInternal,
    shouldConfirmClose,
    onClose: () => setOpenInternal(false),
  });

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      onInteractOutside={onClose}
      contentClassnames="w-full max-w-[35vw]"
      title="Route Template"
    >
      {isLoadingRouteMultiloadTemplates ? (
        <div className="flex flex-row justify-center">
          <LoadingIndicator />
        </div>
      ) : (
        <RouteTemplateForm
          onFormDirty={setFormDirty}
          onCancel={onClose}
          defaultValues={formValues}
        />
      )}
    </Modal>
  );
}
